import React, { useState, useEffect } from 'react';
import { dashboardAPI, referralAPI, dialogsAPI, summariesAPI, stripeAPI } from '../services/api';
import { useAuth } from '../services/AuthContext';
import { useNavigate } from 'react-router-dom';
import ClientRow from '../components/Dashboard/ClientRow';
import CoachSidebar from '../components/Dashboard/CoachSidebar';
import EditAvatarModal from '../components/Modals/EditAvatarModal';
import EditPromptModal from '../components/Modals/EditPromptModal';
import AnalyticsDashboard from '../components/Dashboard/AnalyticsDashboard';

// Dummy data removed to use real API data

const Dashboard = () => {
  const [, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [finances, setFinances] = useState(null);

  useEffect(() => {
    if (user?.role === 'admin') {
      navigate('/admin/coaches', { replace: true });
    }
  }, [user, navigate]);

  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsResponse, referralsResponse, dialogsResponse, summariesResponse, financesResponse] = await Promise.all([
          dashboardAPI.getStats(),
          referralAPI.getMyReferrals(),
          user?.role === 'coach' ? dialogsAPI.getAll({ has_messages: true }) : Promise.resolve({ data: { items: [] } }),
          user?.role === 'coach' ? summariesAPI.getAll({ coach_id: user.id }) : Promise.resolve({ data: { items: [] } }),
          user?.role === 'coach' ? stripeAPI.getFinancesStats() : Promise.resolve({ data: null })
        ]);
        
        setStats(statsResponse.data.stats);
        if (financesResponse?.data) {
          setFinances(financesResponse.data);
        }
        
        const rawClients = referralsResponse.data.referrals || [];
        
        if (user?.role === 'coach') {
          // Group dialogs by clientId
          const dialogsByClient = {};
          const rawDialogsData = dialogsResponse.data;
          const dialogsItems = Array.isArray(rawDialogsData) 
            ? rawDialogsData 
            : (rawDialogsData?.items || rawDialogsData?.data?.items || rawDialogsData?.data || []);
          
          dialogsItems.forEach(d => {
            const userId = (d.user_id || d.userId || '').toString().toLowerCase();
            if (userId) {
              if (!dialogsByClient[userId]) dialogsByClient[userId] = [];
              dialogsByClient[userId].push(d);
            }
          });

          // Group summaries by userId
          const summariesByClient = {};
          const summariesData = summariesResponse.data;
          const summariesItems = Array.isArray(summariesData)
            ? summariesData
            : (summariesData?.items || summariesData?.data?.items || summariesData?.data || []);
          
          summariesItems.forEach(s => {
            const userId = (s.user_id || s.userId || '').toString().toLowerCase();
            if (userId) {
              if (!summariesByClient[userId]) summariesByClient[userId] = [];
              summariesByClient[userId].push(s);
            }
          });

          const enrichedClients = rawClients.map(client => {
            const clientSearchId = (client.clientId || '').toString().toLowerCase();
            const clientDialogs = dialogsByClient[clientSearchId] || [];
            const clientSummaries = summariesByClient[clientSearchId] || [];
            
            // Sort summaries by date desc to get the latest
            if (clientSummaries.length > 0) {
              clientSummaries.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            }
            const latestSummary = clientSummaries[0] || null;

            const visitsCount = clientDialogs.length;
            
            let lastInteraction = 'No sessions yet';
            let lastInteractionType = '-';
            let lastInteractionTypeIcon = 'calendar_today';

            if (visitsCount > 0) {
              // Sort by date desc
              clientDialogs.sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
              const last = clientDialogs[0];
              const date = new Date(last.updated_at || last.created_at);
              lastInteraction = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              
              const source = (last.metadata?.source || 'chat').toLowerCase();
              if (source.includes('voice')) {
                lastInteractionType = 'Voice';
                lastInteractionTypeIcon = 'mic';
              } else if (source.includes('video')) {
                lastInteractionType = 'Video';
                lastInteractionTypeIcon = 'videocam';
              } else {
                lastInteractionType = 'Chat';
                lastInteractionTypeIcon = 'forum';
              }
            }

            return {
              ...client,
              visits: `${visitsCount} visit${visitsCount !== 1 ? 's' : ''}`,
              lastInteractionTime: lastInteraction,
              lastInteractionType,
              lastInteractionTypeIcon,
              latestSummary
            };
          });
          setClients(enrichedClients);
        } else {
          setClients(rawClients);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  if (loading) {
    return (
      <div className="relative flex size-full min-h-screen flex-col bg-[var(--bg-main)] group/design-root overflow-x-hidden font-display">
        <div className="layout-container flex h-full grow flex-col">
          <div className="px-4 flex flex-1 justify-center py-5 w-full">
            <div className="layout-content-container flex flex-col w-full max-w-[960px] flex-1">
              <div className="flex justify-center items-center h-64 w-full">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-tangerine"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (user?.role === 'coach') {
    return (
      <div className="relative flex size-full min-h-screen flex-col bg-[var(--bg-main)] group/design-root overflow-x-hidden font-display text-[var(--text-primary)]">
        
        <EditAvatarModal isOpen={isAvatarModalOpen} onClose={() => setIsAvatarModalOpen(false)} currentAvatar="" />
        <EditPromptModal isOpen={isPromptModalOpen} onClose={() => setIsPromptModalOpen(false)} />

        <div className="layout-container flex h-full grow flex-col">
          <div className="px-4 sm:px-6 md:px-8 lg:px-12 flex flex-1 justify-center py-5 w-full">
            <div className="layout-content-container flex flex-col lg:flex-row w-full max-w-[1200px] flex-1 gap-8">
              
              <main className="flex-1 flex flex-col">
                <header className="py-2 pb-6">
                  <div className="flex justify-between items-end mb-6">
                    <div>
                      <h1 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)] tracking-tight">Clients</h1>
                    </div>
                    <button 
                      onClick={() => setShowAnalytics(!showAnalytics)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                        showAnalytics 
                        ? 'bg-[var(--tangerine)] text-white shadow-lg shadow-tangerine/20' 
                        : 'bg-[var(--surface-white)] text-[var(--text-secondary)] border border-[var(--border-light)] hover:border-[var(--tangerine)]'
                      }`}
                    >
                      <span className="material-symbols-outlined text-[20px]">analytics</span>
                      {showAnalytics ? 'Hide Analytics' : 'Show Analytics'}
                    </button>
                  </div>

                  {showAnalytics && <AnalyticsDashboard coachId={user?.id} />}
                  
                  <div className="flex flex-wrap items-center gap-6 md:gap-8 bg-[var(--surface-white)] p-5 rounded-3xl shadow-xl shadow-black/5 border border-[var(--border-light)]">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-orange-50 flex items-center justify-center text-[var(--tangerine)]">
                        <span className="material-symbols-outlined filled-icon text-xl">groups</span>
                      </div>
                      <div>
                        <p className="text-[10px] sm:text-xs font-bold text-[var(--text-secondary)]/60 uppercase tracking-widest">Total Clients</p>
                        <p className="text-lg sm:text-xl font-bold text-[var(--text-primary)]">{clients.length}</p>
                      </div>
                    </div>
                    <div className="hidden sm:block h-10 w-px bg-[var(--border-light)]/30"></div>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-[var(--off-white)] flex items-center justify-center text-[var(--text-secondary)]/60">
                        <span className="material-symbols-outlined text-xl">payments</span>
                      </div>
                      <div>
                        <p className="text-[10px] sm:text-xs font-bold text-[var(--text-secondary)]/60 uppercase tracking-widest">Revenue</p>
                        <p className="text-lg sm:text-xl font-bold text-[var(--text-primary)]">
                          ${(finances?.projectedPathBRevenue || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </p>
                      </div>
                    </div>
                    <div className="hidden sm:block h-10 w-px bg-[var(--border-light)]/30"></div>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-orange-50 flex items-center justify-center text-[var(--tangerine)]">
                        <span className="material-symbols-outlined text-xl">notification_important</span>
                      </div>
                      <div>
                        <p className="text-[10px] sm:text-xs font-bold text-[var(--text-secondary)]/60 uppercase tracking-widest">Needs Follow-up</p>
                        <p className="text-lg sm:text-xl font-bold text-[var(--text-primary)]">
                          {clients.filter(c => c.followUp && c.followUp.trim() !== '').length}
                        </p>
                      </div>
                    </div>
                  </div>
                </header>
                
                <div className="flex-1 space-y-3">
                  <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-[var(--surface-white)] rounded-xl shadow-sm text-[10px] sm:text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider border border-[var(--border-light)] mb-2">
                    <div className="col-span-12 sm:col-span-5 md:col-span-4">Client</div>
                    <div className="hidden sm:block sm:col-span-4 md:col-span-3">Last Interaction</div>
                    <div className="hidden sm:block sm:col-span-3 md:col-span-5 text-right">Actions</div>
                  </div>
                  
                  {clients.length > 0 ? (
                    clients.map(client => (
                      <ClientRow 
                        key={client.id || client.clientId} 
                        client={client} 
                        insights={client.insights} 
                        memory={client.memory} 
                        summary={client.latestSummary}
                      />
                    ))
                  ) : (
                    <div className="bg-[var(--surface-white)] rounded-3xl border border-[var(--border-light)] p-12 text-center shadow-xl shadow-black/5">
                      <div className="w-16 h-16 bg-[var(--off-white)] rounded-full flex items-center justify-center mx-auto mb-4 text-gray-300">
                        <span className="material-symbols-outlined text-3xl">groups</span>
                      </div>
                      <p className="text-[var(--text-secondary)] font-medium">No clients found yet.</p>
                      <p className="text-xs text-[var(--text-secondary)]/60 mt-1">Share your referral link to invite clients.</p>
                    </div>
                  )}

                  <div className="flex items-center justify-between bg-[var(--surface-white)] rounded-3xl border border-[var(--border-light)] p-4 mt-6 shadow-xl shadow-black/5">
                    <p className="text-xs text-[var(--text-secondary)]">Showing <span className="font-bold text-[var(--text-primary)]">{clients.length > 0 ? 1 : 0}</span> to <span className="font-bold text-[var(--text-primary)]">{clients.length}</span> of <span className="font-bold text-[var(--text-primary)]">{clients.length}</span> results</p>
                    <div className="flex gap-2">
                      <button className="px-3 py-1.5 bg-[var(--off-white)] border border-[var(--border-light)] rounded-lg text-xs font-bold text-[var(--text-secondary)] hover:bg-[var(--off-white)] disabled:opacity-50 transition-colors" disabled>Previous</button>
                      <button className="px-3 py-1.5 bg-[var(--surface-white)] border border-[var(--border-light)] rounded-lg text-xs font-bold text-gray-700 hover:bg-[var(--off-white)] transition-colors">Next</button>
                    </div>
                  </div>
                </div>
              </main>
              
              <CoachSidebar 
                onOpenAvatarModal={() => setIsAvatarModalOpen(true)}
                onOpenPromptModal={() => setIsPromptModalOpen(true)}
                onOpenSettingsModal={() => { window.location.href = '/coach/settings' }}
              />
              
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Client View
  return (
    <div className="relative flex size-full min-h-screen flex-col bg-[var(--bg-main)] group/design-root overflow-x-hidden font-display text-[var(--text-primary)]">
      <div className="layout-container flex h-full grow flex-col">
        <div className="px-4 flex flex-1 justify-center py-5 w-full">
          <div className="layout-content-container flex flex-col w-full max-w-[960px] flex-1">
            <div className="bg-[var(--surface-white)] overflow-hidden shadow-xl shadow-black/5 border border-[var(--border-light)] rounded-3xl p-6 md:p-8 mb-8">
              <h2 className="text-xl font-bold text-[var(--text-primary)] mb-4">
                Welcome to the coaching platform, {user?.name}!
              </h2>
              <p className="text-[var(--text-secondary)] mb-4">
                You can start communicating with coaches in the chat section.
              </p>
              <a href="/user/chat" className="bg-tangerine hover:bg-tangerine/90 text-white font-bold shadow-md shadow-tangerine/20 transition-all hover:-translate-y-0.5 px-4 py-2 rounded-xl inline-block">
                Go to Chat
              </a>
            </div>

            <div className="bg-[var(--surface-white)] shadow-xl shadow-black/5 border border-[var(--border-light)] rounded-3xl p-6 md:p-8">
              <h2 className="text-xl font-bold text-[var(--text-primary)] mb-4">Recent Activities</h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-4 bg-[var(--off-white)] rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-medium">📹</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[var(--text-primary)]">Video session completed</p>
                      <p className="text-xs text-[var(--text-secondary)]">2 days ago</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
